/**
 * Phase 8 · Cluster C-43 — LinkedIn sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * Read-only candidate discovery against LinkedIn. Two auth paths:
 *
 *   - auth_kind='oauth'            → Sales Navigator / Recruiter "people
 *                                    search" REST API with a stored OAuth
 *                                    bearer. Real handshake lands in a
 *                                    follow-up PR (see DON'T in C-43);
 *                                    until then we document the call but
 *                                    route through the Edge Function
 *                                    proxy that also does the decryption.
 *   - auth_kind='session_cookie'   → self-hosted `linkedin-api` proxy
 *                                    receives `li_at` cookie + returns
 *                                    JSON. URL is read from
 *                                    `config_json.proxy_url`.
 *
 * Neither path runs direct from the browser in production because
 * LinkedIn's origin rejects `Access-Control-Allow-Origin`. We attempt
 * the Supabase Edge Function proxy (`ggj_b2b_sourcing_proxy`) first and
 * fall back to a `ConnectorTransportError` when that function is not
 * deployed (today's state). That keeps the tool chain honest — a
 * "couldn't reach LinkedIn" surface beats a silently empty result.
 *
 * Rate limiting: an in-memory token bucket per (org, kind) at 50 searches
 * / hour. This is process-local; a Redis-backed limiter is TODO (`F7
 * hardening`).
 */
import { supabase } from '@core/supabase/client';
import { sourceConnectorsService } from '@core/supabase/services/sourceConnectorsService';
import {
  clampLimit,
  ConnectorNotConfiguredError,
  ConnectorTransportError,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

const HOURLY_LIMIT = 50;
const WINDOW_MS = 60 * 60 * 1000;
const PROXY_FN = 'ggj_b2b_sourcing_proxy';

interface BucketState {
  timestamps: number[];
}
const rateBuckets = new Map<string, BucketState>();

/**
 * Sliding-window rate limit. Returns `true` when the call may proceed,
 * `false` when the bucket is full. We keep the math tiny (one push + one
 * filter) so this hot path stays allocation-free in the happy case.
 */
function acquireToken(key: string): boolean {
  const now = Date.now();
  const state = rateBuckets.get(key) ?? { timestamps: [] };
  state.timestamps = state.timestamps.filter((t) => now - t < WINDOW_MS);
  if (state.timestamps.length >= HOURLY_LIMIT) {
    rateBuckets.set(key, state);
    return false;
  }
  state.timestamps.push(now);
  rateBuckets.set(key, state);
  return true;
}

interface ProxyPeopleResponse {
  people: Array<{
    externalId?: string;
    id?: string;
    name?: string;
    fullName?: string;
    headline?: string;
    locationText?: string;
    location?: string;
    profileUrl?: string;
    url?: string;
    avatarUrl?: string;
    signals?: Record<string, unknown>;
  }>;
}

function buildKeywords(q: SourcingQuery): string {
  const bits = [
    ...(q.keywords ?? []),
    ...(q.skills ?? []),
  ];
  return bits.map((s) => s.trim()).filter(Boolean).join(' ');
}

export const linkedinConnector: SourcingConnector = {
  kind: 'linkedin',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];

    const row = await sourceConnectorsService.get(orgId, 'linkedin');
    if (!row) throw new ConnectorNotConfiguredError('linkedin');
    if (row.status === 'expired') {
      throw new ConnectorNotConfiguredError('linkedin', 'credential expired — reconnect');
    }
    if (row.status === 'revoked') {
      throw new ConnectorNotConfiguredError('linkedin', 'revoked by admin');
    }
    if (row.authKind !== 'oauth' && row.authKind !== 'session_cookie') {
      throw new ConnectorNotConfiguredError(
        'linkedin',
        `unsupported auth_kind "${row.authKind}"`,
      );
    }

    if (!acquireToken(`linkedin:${orgId}`)) {
      throw new ConnectorTransportError(
        'linkedin',
        `rate limit exceeded (${HOURLY_LIMIT}/hour) — try again soon`,
      );
    }

    const limit = clampLimit(query.limit);
    const keywords = buildKeywords(query);

    // Route through the Edge Function proxy so the credential decryption
    // stays server-side. If the proxy isn't deployed yet the `.invoke`
    // call returns a FunctionsFetchError which we convert to a transport
    // error — the runner emits a clean tool_result.ok=false and the
    // audit row records the failure.
    let payload: ProxyPeopleResponse;
    try {
      const { data, error } = await supabase.functions.invoke(PROXY_FN, {
        body: {
          action: 'linkedin.search',
          orgId,
          query: {
            keywords,
            location: query.location ?? null,
            experienceYearsMin: query.experienceYearsMin ?? null,
            experienceYearsMax: query.experienceYearsMax ?? null,
            limit,
          },
        },
      });
      if (error) throw error;
      payload = (data ?? { people: [] }) as ProxyPeopleResponse;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'proxy unavailable';
      throw new ConnectorTransportError('linkedin', msg);
    }

    // Fire-and-forget last-used stamp.
    void sourceConnectorsService.markUsed(orgId, 'linkedin');

    const rows = Array.isArray(payload?.people) ? payload.people : [];
    return rows.slice(0, limit).map((p) => ({
      externalId: String(p.externalId ?? p.id ?? ''),
      name: p.name ?? p.fullName ?? '(unknown)',
      headline: p.headline,
      locationText: p.locationText ?? p.location ?? undefined,
      profileUrl: p.profileUrl ?? p.url ?? 'https://www.linkedin.com/',
      avatarUrl: p.avatarUrl ?? undefined,
      signals: (p.signals && typeof p.signals === 'object' ? p.signals : {}) as Record<string, unknown>,
      source: 'linkedin',
    }));
  },

  async healthcheck(orgId: string): Promise<ConnectorHealth> {
    const row = await sourceConnectorsService.get(orgId, 'linkedin');
    if (!row) return { ok: false, reason: 'not configured' };
    if (row.status !== 'active') return { ok: false, reason: row.status };
    return { ok: true };
  },
};
